import axios, { AxiosResponse, AxiosError } from 'axios'
import { QueryFunctionContext } from '@tanstack/react-query'
import { getInstance } from './attachInstance'
import {
  PrebidProviderData,
  PrebidChildSegmentData,
  PrebidSearchSegmentsData,
} from '../models/PreBidAudience'
import {
  transformChildSegmentData,
  transformParentProviderData,
  constructProviderDataFromSearchResults,
} from '../components/SelectPreBidSegment/helpers'
import { getPrebidSearchSegmentDataKey } from './queryKeys'
import { WithPreBidAudienceResponse } from '../models/Response'

// Fetch the parent provider details and transform
export const getPrebidProviderData = async () => {
  try {
    const response: AxiosResponse<
      WithPreBidAudienceResponse<PrebidProviderData[]>
    > = await getInstance().get('/v3/audience/prebid/provider/details/list')
    const { totalRecords, filteredRecords, prebidSegmentData } =
      response.data.data

    return {
      totalRecords,
      filteredRecords,
      prebidSegmentData: transformParentProviderData(prebidSegmentData),
    }
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response?.data)
  }
}

export const getPrebidChildSegmentDetailByParentId = async ({
  prebidAudienceSegmentId,
  providerDetails,
}: {
  prebidAudienceSegmentId: number
  providerDetails: PrebidProviderData
}) => {
  try {
    const response: AxiosResponse<
      WithPreBidAudienceResponse<PrebidChildSegmentData[]>
    > = await getInstance().get('/v3/audience/prebid/segment/details/list', {
      params: { prebidAudienceSegmentId },
    })
    const { totalRecords, filteredRecords, prebidSegmentData } =
      response.data.data

    return {
      totalRecords,
      filteredRecords,
      prebidSegmentData: transformChildSegmentData(
        prebidSegmentData,
        providerDetails
      ),
    }
  } catch (e) {
    return Promise.reject((e as AxiosError)?.response?.data)
  }
}

export const getPrebidSearchSegmentData = async ({
  queryKey,
  signal,
  meta,
}: QueryFunctionContext<
  ReturnType<(typeof getPrebidSearchSegmentDataKey)['keys']>
>) => {
  const { searchValue } = queryKey[0]!
  const { CancelToken } = axios
  const source = CancelToken.source()
  signal?.addEventListener('abort', () => {
    source.cancel('v3/audience/prebid/details/search/list - Request cancelled')
  })
  try {
    const response: AxiosResponse<
      WithPreBidAudienceResponse<PrebidSearchSegmentsData[]>
    > = await getInstance().post(
      '/v3/audience/prebid/details/search/list',
      { searchField: searchValue },
      {
        cancelToken: source.token,
      }
    )
    const { prebidSegmentData, totalRecords, filteredRecords } =
      response.data.data

    return {
      totalRecords,
      filteredRecords,
      prebidSegmentData: constructProviderDataFromSearchResults(
        prebidSegmentData,
        meta as any
      ),
    }
  } catch (e) {
    return Promise.reject((e as AxiosError).response?.data)
  }
}
